import type {HeaderResponse} from '@github-ui/code-view-header/types'
import type {CopilotSurveyProps} from '@github-ui/copilot-survey-card'
import type {SafeRailsHTML} from '@github-ui/rails-partial'
import type {DataRouterFilePagePayload} from './code-view-types'
import type {DataRouterFileTreePagePayload, ReadmeBlobPayload} from './tree-types'

export interface DataRouterFileOverviewPayload extends DataRouterFileTreePagePayload {
  overview: OverviewPayload
}

export function isFileOverviewPayload(payload: DataRouterFilePagePayload): payload is DataRouterFileOverviewPayload {
  return 'overview' in payload
}

export type BannerSegment = {type: 'text'; content: string} | {type: 'link'; content: string; href: string}

export interface BannerAnalytics {
  category: string
  group: string
  location: string
}

export interface TransactionalMessageBanner {
  segments: BannerSegment[]
  dismissPath: string
  analytics: BannerAnalytics
  /**
   * Optional call-to-action button rendered in Primer Banner's `primaryAction` slot.
   * When present, both `label` and `href` must be set; backend omits the entire field
   * if either is nil to avoid a half-rendered button reaching the wire.
   * Click is tracked via `data-analytics-event` with `action: 'click'`, using the
   * same `category`/`group`/`location` label format as the visible/dismiss events.
   */
  cta?: {
    label: string
    href: string
  }
}

export interface BlockedContributor {
  login: string
  name: string
  avatarUrl: string
  commitsPath: string
}

export interface BlockedContributorsBanner {
  totalCount: number
  contributors: BlockedContributor[]
  additionalLogins: string[]
  manageBlockedUsersPath: string
  disableWarningPath: string
}

export interface OverviewPayload {
  codeButton: CodeButton
  createFromTemplatePath: string
  banners: {
    shouldRecommendReadme: boolean
    isPersonalRepo: boolean
    showUseActionBanner: boolean
    actionSlug: string | undefined
    actionId: number | undefined
    showProtectBranchBanner: boolean | undefined
    transactionalMessageBanner?: TransactionalMessageBanner | null
    copilotSurveyBanner?: Omit<CopilotSurveyProps, 'icon'> | null
    publishBannersInfo: {
      dismissActionNoticePath: string
      releasePath: string
      showPublishActionBanner: boolean
    }
    interactionLimitBanner?: InteractionLimitBanner
    showInvitationBanner: boolean
    inviterName: string | undefined
    actionsMigrationBannerInfo: {
      releaseTags: string[]
      showImmutableActionsMigrationBanner: boolean
      initialMigrationStatus: string | null
    }
    showDeployBanner: boolean
    detectedStack?: {
      framework: string | null
      packageManager: string | null
    }
    showSpammyBanner?: boolean
    blockedContributorsBanner?: BlockedContributorsBanner | null
  }
  commitCount: string
  overviewFiles: RepoOverviewBlobPayload[] | undefined
  popovers: {
    rename: BranchRename | null
    renamedParentRepo: {
      nameWithOwner: string
      branchName: string
    } | null
  }
  templateButton?: {
    codespacesPath: string
    templateRepoId: string
    templateRef: string
  }
  copilotSWEAgentEnabled: boolean
  partials: {
    header: SafeRailsHTML
    sidebar: SafeRailsHTML
  }
  /** Sidebar about data embedded in the main payload for tree-route overview views,
   * avoiding a separate fetch. */
  sidebarAboutData?: HeaderResponse
}

export interface CodeButton {
  codespacesEnabled: boolean
  hasAccessToCodespaces: boolean
  repoPolicyInfo?: RepoPolicyInfo
  contactPath: string
  currentUserIsEnterpriseManaged: boolean
  enterpriseManagedBusinessName?: string
  isEnterprise: boolean
  newCodespacePath?: string
  local: {
    protocolInfo: {
      httpAvailable: boolean
      sshAvailable: boolean
      httpUrl: string
      showCloneWarning: boolean
      sshUrl: string
      sshCertificatesRequired: boolean
      sshCertificatesAvailable: boolean
      ghCliUrl: string
      defaultProtocol: string
      newSshKeyUrl: string
      setProtocolPath: string
    }
    platformInfo: {
      cloneUrl: string
      visualStudioCloneUrl: string
      xcodeCloneUrl: string
      zipballUrl: string
      showVisualStudioCloneButton: boolean
      showXcodeCloneButton: boolean
    }
  }
}

export interface RepoPolicyInfo {
  allowed: boolean
  canBill: boolean
  hasIpAllowLists: boolean
  disabledByBusiness: boolean
  disabledByOrganization: boolean
  changesWouldBeSafe: boolean
}

export interface BranchRename {
  oldName: string
  shellOldName: string
  newName: string
  shellNewName: string
  shellEscapingDocsURL?: string
}

export interface InteractionLimitBanner {
  adminLink: string | null
  adminText: string | null
  currentExpiry: string
  disablePath: string | null
  limitTitle: string
  inOrganization: boolean
  usersHaveAccess: boolean
  contributorsHaveAccess: boolean
}

export interface RecentlyTouchedBranch {
  branchName: string
  comparePath: string
  date: string
  repoName: string
  repoOwner: string
}

export const PreferredFileTypes = {
  README: 'readme',
  CODE_OF_CONDUCT: 'code_of_conduct',
  CONTRIBUTING: 'contributing',
  LICENSE: 'license',
  SECURITY: 'security',
} as const

export type PreferredFileTypes = (typeof PreferredFileTypes)[keyof typeof PreferredFileTypes]

export interface RepoOverviewBlobPayload extends ReadmeBlobPayload {
  path: string
  refName: string
  repoName: string
  preferredFileType: PreferredFileTypes
  tabName?: string
  loaded: boolean
}
